import React from 'react';
import ChatPage from 'scenes/Chat_page';
import EntrypointPage from 'scenes/MobileEntrypoint_page';
import MainPage from '../../scenes/Main_page';
import RootPage from '../../scenes/Root_page';
import AppbarMenu from '../components/Appbar';

import { PageTypeEnum } from 'constants/pageType';
import PermissionsEnum from 'constants/permissions';
import { TaskType } from 'constants/taskStatus';
import _ from 'lodash';
import GameScene from 'scenes/GameScene_page';
import { arrayToStr, varToStringParams } from 'utils/path';

const isMobile = window.innerWidth <= 425;

const SecurityPage = React.lazy(() => import('scenes/Security_page'));
const ResetPasswordPage = React.lazy(() => import('scenes/ResetPassword_page'));
const ForgotPasswordPage = React.lazy(
  () => import('scenes/ForgotPassword_page')
);
// const TempPage = React.lazy(() => import('scenes/Temp_page'));

const ProductsPage = isMobile
  ? React.lazy(() => import('scenes/Products_page_mobile'))
  : React.lazy(() => import('scenes/Products_page'));
const ShowBlogPage = React.lazy(() => import('scenes/ShowBlog_page'));
const ShowNewsPage = React.lazy(() => import('scenes/ShowNews_page'));
const ShowBlogDetailPage = React.lazy(
  () => import('scenes/ShowBlogDetail_page')
);

const ShowNewsDetailPage = React.lazy(
  () => import('scenes/ShowNewsDetail_page')
);

const ShowTopicDetailPage = React.lazy(
  () => import('scenes/ShowTopicDetail_page')
);

const ProductPrintPdf = React.lazy(() => import('scenes/ProductPrintPdf_page'));

// product pages
const ProductEditPage = React.lazy(() => import('scenes/ProductEdit_page'));
const AccountSettingsPage = React.lazy(
  () => import('scenes/AccountSettings_page')
);

const ProductEditImagesPage = React.lazy(
  () => import('scenes/ProductEdit-Images_page')
);
const ProductEditDocumentsPage = React.lazy(
  () => import('scenes/ProductEdit-Documents_page')
);
const ProductEditNotesPage = React.lazy(
  () => import('scenes/ProductEdit-Notes_page')
);
const ProductEditBasicDetailsPage = React.lazy(
  () => import('scenes/ProductEdit-Basicdetails_page')
);

// settings pages
const SettingPage = React.lazy(() => import('scenes/Setting_page'));
const SettingGeneralPage = React.lazy(
  () => import('scenes/Setting_General_page')
);
const SettingAdditionalPage = React.lazy(
  () => import('scenes/Setting_Additional_page')
);

// task pages
const TaskManagementPage = React.lazy(() => import('scenes/Task_page'));
const TaskListPage = React.lazy(() => import('scenes/Task-List_page'));
const TaskCalendarPage = React.lazy(() => import('scenes/Task-Calendar_page'));
const TaskMapPage = React.lazy(() => import('scenes/Task-Map_page'));
const TaskBoardPage = React.lazy(() => import('scenes/Task-Board_page/index'));
const TopicsPage = React.lazy(() => import('scenes/Topics_page/index'));
const BlogsPage = React.lazy(() => import('scenes/Blogs_page/index'));
const StoresPage = React.lazy(() => import('scenes/Stores_page/index'));

const TopicEditPage = React.lazy(() => import('scenes/TopicEdit_page/index'));
const BlogEditPage = React.lazy(() => import('scenes/BlogEdit_page/index'));
const StoreEditPage = React.lazy(() => import('scenes/StoresEdit_page/index'));

// single client pages
const ClientListingPage = React.lazy(() => import('scenes/ClientListing_page'));
const SingleClientPage = React.lazy(() => import('scenes/SingleClient_page'));
const SingleClientClientDetailsPage = React.lazy(
  () => import('scenes/SingleClient-Clientdetails_page')
);
const SingleClientPropertyCollectionsPage = React.lazy(
  () => import('scenes/SingleClient-Propertycollections_page')
);
const SingleClientRequestPage = React.lazy(
  () => import('scenes/SingleClient-ClientRequest_page')
);

const SingleClientSavedSearchPage = React.lazy(
  () => import('scenes/SingleClient-Savedsearch_page')
);

const SingleClientLocationPage = React.lazy(
  () => import('scenes/SingleClient-ClientLocation')
);

const SingleClientTaskhPage = React.lazy(
  () => import('scenes/SingleClient-Task_page')
);

const SingleClientPropertyCollectionsItemPage = React.lazy(
  () => import('scenes/SingleClient-Propertycollections-Item_page')
);
const SingleClientInternalNotesPage = React.lazy(
  () => import('scenes/SingleClient-InternalNotes_page')
);
const SingleClientPublicNotesPage = React.lazy(
  () => import('scenes/SingleClient-PublicNotes_page')
);
const SingleClientDocumentsPage = React.lazy(
  () => import('scenes/SingleClient-Documents_page')
);

const SingleClientConversationsPage = React.lazy(
  () => import('scenes/SingleClient-Conversations_page')
);

// home page
const HomePage = React.lazy(() => import('scenes/Home_page'));

// Product Details Page
const ProductDetails = React.lazy(() => import('scenes/ProductDetails_page'));

// Client Account Page
const ClientAccountPage = React.lazy(() => import('scenes/ClientAccount_page'));
const ClientAccountProfilePage = React.lazy(
  () => import('scenes/ClientAccount-Profile_page')
);
const ClientAccountFavoritesPage = React.lazy(
  () => import('scenes/ClientAccount-Favorites_page')
);
const ClientAccountAppointmentsPage = React.lazy(
  () => import('scenes/ClientAccount-Appointment_page')
);
const ClientAccountSavedSearchesPage = React.lazy(
  () => import('scenes/ClientAccount-SavedSearches_page')
);

// Category page
const CategoryEditPage = React.lazy(() => import('scenes/CategoryEdit_page'));

// Specification Attributes page
const SpecificationAttributeEditPage = React.lazy(
  () => import('scenes/SpecificationAttributeEdit_page')
);

const DashboardPage = React.lazy(() => import('scenes/Dashboard_page'));

// News page
const NewsListPage = React.lazy(() => import('scenes/News_page'));
const NewEditPage = React.lazy(() => import('scenes/NewEdit_page'));

// Document page
const DocumentListPage = React.lazy(() => import('scenes/Documents_page'));
const DocumentEditPage = React.lazy(() => import('scenes/DocumentEdit_page'));

// Reporting page
const ReportingListPage = React.lazy(() => import('scenes/Reporting_page'));
export interface IPage {
  path: string;
  component: any;
  permission?: PermissionsEnum;
  requiredLogin?: boolean;
  pageType?: PageTypeEnum;
}

interface INavigationConfig {
  rootPage: () => IPage;
  mainPage: () => IPage;
  forgotPasswordPage: () => IPage;

  resetPasswordPage: (token?: string) => IPage;
  securityPage: () => IPage;
  mobileEntrypointPage: () => IPage;
  dashboardPage: () => IPage;

  productsPage: (params?: {
    guid?: string;
    sortType?: string;
    viewType?: string;
    tlc_lat?: string;
    tlc_lng?: string;
    trc_lat?: string;
    trc_lng?: string;
    blc_lat?: string;
    blc_lng?: string;
    brc_lat?: string;
    brc_lng?: string;
    page?: string;
    type?: number;
  }) => IPage;
  showBlogPage: () => IPage;
  showNewsPage: () => IPage;
  showBlogDetailPage: (params?: { guid?: string }) => IPage;
  showNewsDetailPage: (params?: { guid?: string }) => IPage;
  showTopicDetailPage: (params?: { guid?: string }) => IPage;
  productPrintPdf: () => IPage;
  productEditPage: (params?: { itemId: string }) => IPage;
  accountSettingPage: (params?: { itemId: string }) => IPage;
  productEditBasicDetailsPage: (params?: { itemId: string }) => IPage;
  productEditImagesPage: (params?: { itemId: string }) => IPage;
  productEditDocumentsPage: (params?: { itemId: string }) => IPage;
  productEditNotesPage: (params?: { itemId: string }) => IPage;
  tasksPage: () => IPage;
  tasksList: (params?: { itemId?: string; taskType?: TaskType }) => IPage;
  tasksCalendarPage: () => IPage;
  tasksMapPage: (params?: { lat: number; lng: number }) => IPage;
  taskBoardPage: () => IPage;

  clientListingPage: (params?: {
    clientTypes?: string[];
    addedAgent?: string[];
  }) => IPage;
  singleClientPage: (params?: { clientId: string }) => IPage;
  singleClientSavedSearchPage: (params?: { clientId: string }) => IPage;
  singleClientLocationPage: (params?: { clientId: string }) => IPage;
  singleClientTaskPage: (params?: { clientId: string }) => IPage;
  singleClientClientDetailsPage: (params?: { clientId: string }) => IPage;
  singleClientPropertyCollectionsPage: (params?: { clientId: string }) => IPage;
  singleClientRequestPage: (params?: { clientId: string }) => IPage;
  singleClientPropertyCollectionsItemPage: (params?: {
    clientId: string;
    itemId: string;
  }) => IPage;
  singleClientInternalNotesPage: (params?: { clientId: string }) => IPage;
  singleClientPublicNotesPage: (params?: { clientId: string }) => IPage;
  singleClientDocumentsPage: (params?: { clientId: string }) => IPage;
  singleClientConversationsPage: (params?: {
    clientId: string;
    chatGuid?: string;
  }) => IPage;

  storesPage: () => IPage;
  settingsPage: (params?: { itemId: string }) => IPage;
  settingsGeneralPage: (params?: { itemId: string }) => IPage;
  settingsAdditionalPage: (params?: { itemId: string }) => IPage;
  topicsPage: () => IPage;
  blogsPage: () => IPage;
  topicEditPage: (params?: { guid?: string }) => IPage;
  blogEditPage: (params?: { guid?: string }) => IPage;
  storesEditPage: (params?: { id?: number }) => IPage;

  homePage: (params?: { type?: string }) => IPage;
  productDetailsPage: (params?: { itemId: string }) => IPage;
  clientAccountPage: () => IPage;
  clientAccountProfilePage: () => IPage;
  clientAccountFavoritesPage: (params?: { showCollection: boolean }) => IPage;
  clientAccountAppointmentsPage: () => IPage;
  clientAccountSavedSearchesPage: () => IPage;
  clientAccountRecommendationPage: () => IPage;
  chatPage: (params?: { chatGuid: string }) => IPage;
  categoryEditPage: (params?: { itemId: string }) => IPage;
  specificationAttributeEditPage: (params?: {
    itemId: string;
    isGeneral?: boolean;
  }) => IPage;
  newsListPage: () => IPage;
  newEditPage: (params?: { itemId: string }) => IPage;
  documentListPage: () => IPage;
  documentEditPage: (params?: { itemId: string }) => IPage;
  reportingListPage: (params?: {
    guid?: string;
    sortType?: string;
    viewType?: string;
    page?: string;
    type?: number;
  }) => IPage;
  gameScenePage: () => IPage;
}

interface IAppbarsConfig {
  primaryNav: INav;
  secondaryNav: INav;
}

interface INav {
  component: any;
  include: 'all' | string[] | null;
  exclude: 'all' | string[] | null;
  select: 'include' | 'exclude';
}

const NavigationConfig: INavigationConfig = {
  rootPage: (params?: any): IPage => ({
    path: '*',
    component: RootPage,
  }),
  mainPage: (params?: any) => ({
    path: `/`,
    component: MainPage,
  }),
  productDetailsPage: ({ itemId } = { itemId: ':itemId' }) => ({
    path: `/product/details/${itemId}`,
    component: ProductDetails,
    pageType: PageTypeEnum.Shop,
  }),
  forgotPasswordPage: () => ({
    path: '/forgot',
    component: ForgotPasswordPage,
  }),
  resetPasswordPage: (token = ':token') => ({
    path: `/reset-password/${token}`,
    component: ResetPasswordPage,
  }),
  securityPage: () => ({
    path: `/security`,
    component: SecurityPage,
    requiredLogin: true,
  }),
  mobileEntrypointPage: (params?: any): IPage => ({
    path: '/mobile/entrypoint',
    component: EntrypointPage,
    requiredLogin: true,
  }),
  dashboardPage: (params?: any): IPage => ({
    path: '/dashboard',
    component: DashboardPage,
    permission: PermissionsEnum.NavigationDashboard,
    requiredLogin: true,
    pageType: PageTypeEnum.Shop,
  }),
  productsPage: (params): IPage => ({
    path: `/listings${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: ProductsPage,
    pageType: PageTypeEnum.Shop,
    requiredLogin: true,
  }),

  accountSettingPage: (): IPage => ({
    path: `/account-setting`,
    component: AccountSettingsPage,
    permission: PermissionsEnum.AccountSetting,
    requiredLogin: true,
    pageType: PageTypeEnum.Shop,
  }),
  /* edit-product routes */
  productEditPage: ({ itemId } = { itemId: ':itemId' }): IPage => ({
    path: `/edit-product/${itemId}`,
    component: ProductEditPage,
    permission: PermissionsEnum.ProductUpdate,
    requiredLogin: true,
    pageType: PageTypeEnum.Shop,
  }),
  productEditBasicDetailsPage: ({ itemId } = { itemId: ':itemId' }) => ({
    path: `/edit-product/${itemId}/basic-details`,
    component: ProductEditBasicDetailsPage,
    permission: PermissionsEnum.ProductUpdate,
    requiredLogin: true,
    pageType: PageTypeEnum.Shop,
  }),
  productEditNotesPage: ({ itemId } = { itemId: ':itemId' }): IPage => ({
    path: `/edit-product/${itemId}/notes`,
    component: ProductEditNotesPage,
    permission: PermissionsEnum.ProductUpdate,
    requiredLogin: true,
    pageType: PageTypeEnum.Shop,
  }),
  productEditImagesPage: ({ itemId } = { itemId: ':itemId' }): IPage => ({
    path: `/edit-product/${itemId}/images`,
    component: ProductEditImagesPage,
    permission: PermissionsEnum.ProductUpdate,
    requiredLogin: true,
    pageType: PageTypeEnum.Shop,
  }),
  productEditDocumentsPage: ({ itemId } = { itemId: ':itemId' }): IPage => ({
    path: `/edit-product/${itemId}/documents`,
    component: ProductEditDocumentsPage,
    permission: PermissionsEnum.ProductUpdate,
    requiredLogin: true,
    pageType: PageTypeEnum.Shop,
  }),

  /* task routes */
  tasksPage: (params?: any): IPage => ({
    path: '/tasks',
    component: TaskManagementPage,
    permission: PermissionsEnum.NavigationTasks,
    requiredLogin: true,
  }),
  tasksList: (params?: { itemId?: string; taskType?: TaskType }): IPage => ({
    path: `/tasks/list${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: TaskListPage,
    requiredLogin: true,
  }),
  tasksCalendarPage: (): IPage => ({
    path: `/tasks/calendar`,
    component: TaskCalendarPage,
    requiredLogin: true,
  }),
  tasksMapPage: (params?: { lat: number; lng: number }): IPage => ({
    path: `/tasks/map${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: TaskMapPage,
    requiredLogin: true,
  }),
  taskBoardPage: (): IPage => ({
    path: `/tasks/board`,
    component: TaskBoardPage,
    requiredLogin: true,
  }),

  storesPage: (params?: any): IPage => ({
    path: '/stores',
    component: StoresPage,
    permission: PermissionsEnum.NavigationStores,
    requiredLogin: true,
  }),

  settingsPage: (): IPage => ({
    path: `/account-setting/settings`,
    component: SettingPage,
    permission: PermissionsEnum.NavigationSettings,
    requiredLogin: true,
  }),
  settingsGeneralPage: () => ({
    path: `/account-setting/settings/general`,
    component: SettingGeneralPage,
    permission: PermissionsEnum.NavigationSettings,
    requiredLogin: true,
  }),
  settingsAdditionalPage: () => ({
    path: `/account-setting/settings/additional`,
    component: SettingAdditionalPage,
    permission: PermissionsEnum.NavigationSettings,
    requiredLogin: true,
  }),
  topicsPage: (params?: any): IPage => ({
    path: '/account-setting/topic',
    component: TopicsPage,
    permission: PermissionsEnum.NavigationTopic,
    requiredLogin: true,
  }),

  blogsPage: (params?: any): IPage => ({
    path: '/account-setting/blog',
    component: BlogsPage,
    permission: PermissionsEnum.NavigationBlog,
    requiredLogin: true,
  }),

  topicEditPage: (params): IPage => ({
    path: `/topic/edit${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: TopicEditPage,
    permission: PermissionsEnum.NavigationTopic,
    requiredLogin: true,
  }),

  blogEditPage: (params): IPage => ({
    path: `/blog/edit${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: BlogEditPage,
    permission: PermissionsEnum.NavigationBlog,
    requiredLogin: true,
  }),
  storesEditPage: (params): IPage => ({
    path: `/stores/edit${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: StoreEditPage,
    permission: PermissionsEnum.NavigationStores,
    requiredLogin: true,
  }),

  showBlogPage: (): IPage => ({
    path: `/show_blog`,
    component: ShowBlogPage,
  }),
  showNewsPage: (): IPage => ({
    path: `/show_news`,
    component: ShowNewsPage,
    permission: PermissionsEnum.NavigationShowBlogPage,
  }),
  showBlogDetailPage: (params): IPage => ({
    path: `/show_detail_blog${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: ShowBlogDetailPage,
    permission: PermissionsEnum.NavigationShowBlogPage,
  }),

  showNewsDetailPage: (params): IPage => ({
    path: `/show_detail_news${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: ShowNewsDetailPage,
    permission: PermissionsEnum.NavigationShowBlogPage,
  }),

  showTopicDetailPage: (params): IPage => ({
    path: `/show_detail_topic${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: ShowTopicDetailPage,
    permission: PermissionsEnum.NavigationShowBlogPage,
  }),

  productPrintPdf: (): IPage => ({
    path: `/product-print-pdf/`,
    component: ProductPrintPdf,
    pageType: PageTypeEnum.Shop,
  }),

  /** Single Client routes */
  clientListingPage: (params): IPage => ({
    path: `/clients${varToStringParams({
      variablesArray: [
        {
          key: 'clientTypes',
          value: arrayToStr(params?.clientTypes ?? []),
        },
        {
          key: 'addedAgent',
          value: arrayToStr(params?.addedAgent ?? []),
        },
      ],
    })}`,
    component: ClientListingPage,
    permission: PermissionsEnum.NavigationClients,
    requiredLogin: true,
  }),
  singleClientPage: ({ clientId } = { clientId: ':clientId' }): IPage => ({
    path: `/clients/${clientId}`,
    component: SingleClientPage,
    requiredLogin: true,
  }),
  singleClientClientDetailsPage: (
    { clientId } = { clientId: ':clientId' }
  ): IPage => ({
    path: `/clients/${clientId}/details`,
    component: SingleClientClientDetailsPage,
    requiredLogin: true,
  }),
  singleClientPropertyCollectionsPage: (
    { clientId } = { clientId: ':clientId' }
  ): IPage => ({
    path: `/clients/${clientId}/collections`,
    component: SingleClientPropertyCollectionsPage,
    requiredLogin: true,
  }),
  singleClientRequestPage: (
    { clientId } = { clientId: ':clientId' }
  ): IPage => ({
    path: `/clients/${clientId}/client-request`,
    component: SingleClientRequestPage,
    requiredLogin: true,
  }),
  singleClientSavedSearchPage: (
    { clientId } = { clientId: ':clientId' }
  ): IPage => ({
    path: `/clients/${clientId}/saved_searches`,
    component: SingleClientSavedSearchPage,
    requiredLogin: true,
  }),
  singleClientLocationPage: (
    { clientId } = { clientId: ':clientId' }
  ): IPage => ({
    path: `/clients/${clientId}/location`,
    component: SingleClientLocationPage,
    requiredLogin: true,
  }),

  singleClientTaskPage: ({ clientId } = { clientId: ':clientId' }): IPage => ({
    path: `/clients/${clientId}/task`,
    component: SingleClientTaskhPage,
    requiredLogin: true,
  }),
  singleClientPropertyCollectionsItemPage: (
    { clientId, itemId } = { clientId: ':clientId', itemId: ':itemId' }
  ): IPage => ({
    path: `/clients/${clientId}/collections/${itemId}`,
    component: SingleClientPropertyCollectionsItemPage,
    requiredLogin: true,
  }),
  singleClientInternalNotesPage: (
    { clientId } = { clientId: ':clientId' }
  ) => ({
    path: `/clients/${clientId}/notes`,
    component: SingleClientInternalNotesPage,
    requiredLogin: true,
  }),
  singleClientPublicNotesPage: ({ clientId } = { clientId: ':clientId' }) => ({
    path: `/clients/${clientId}/public_notes`,
    component: SingleClientPublicNotesPage,
    requiredLogin: true,
  }),
  singleClientDocumentsPage: ({ clientId } = { clientId: ':clientId' }) => ({
    path: `/clients/${clientId}/documents`,
    component: SingleClientDocumentsPage,
    requiredLogin: true,
  }),
  singleClientConversationsPage: (
    { clientId, chatGuid } = { clientId: ':clientId', chatGuid: ':chatGuid?' }
  ) => ({
    path: `/clients/${clientId}/conversations${chatGuid ? `/${chatGuid}` : ''}`,
    component: SingleClientConversationsPage,
    requiredLogin: true,
  }),
  homePage: (params): IPage => ({
    path: `/home${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: HomePage,
    permission: PermissionsEnum.NavigationHome,
  }),
  /** Client Account Routes */
  clientAccountPage: (): IPage => ({
    path: `/account`,
    component: null,
    requiredLogin: true,
  }),
  clientAccountProfilePage: () => ({
    path: `/account/profile`,
    component: ClientAccountProfilePage,
    permission: PermissionsEnum.NavigationProfile,
    requiredLogin: true,
  }),
  clientAccountFavoritesPage: (params): IPage => ({
    path: `/account/favorites${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: ClientAccountFavoritesPage,
    requiredLogin: true,
  }),
  clientAccountAppointmentsPage: () => ({
    path: `/account/appointments`,
    component: ClientAccountAppointmentsPage,
    requiredLogin: true,
  }),

  clientAccountSavedSearchesPage: () => ({
    path: `/account/save-searches`,
    component: ClientAccountSavedSearchesPage,
    requiredLogin: true,
  }),

  clientAccountRecommendationPage: () => ({
    path: `/account/recommendations`,
    component: React.lazy(() => import('scenes/Temp_page')),
    requiredLogin: true,
  }),
  /** Chat Account Routes */
  chatPage: ({ chatGuid } = { chatGuid: ':chatGuid?' }) => ({
    path: `/chat/${chatGuid}`,
    component: ChatPage,
    requiredLogin: true,
  }),
  /** Category */
  categoryEditPage: ({ itemId } = { itemId: ':itemId' }): IPage => ({
    path: `/edit-category/${itemId}`,
    component: CategoryEditPage,
    permission: PermissionsEnum.CategoryUpdate,
    requiredLogin: true,
  }),
  /** Specification Attributes */
  specificationAttributeEditPage: (
    { itemId, isGeneral } = { itemId: ':itemId' }
  ): IPage => ({
    path: `/edit-specification-attribute/${itemId}${
      isGeneral ? '?isGeneral=true' : ''
    }`,
    component: SpecificationAttributeEditPage,
    permission: PermissionsEnum.NavigationSpecificationAttributes,
    requiredLogin: true,
  }),
  /** Specification Attributes */
  newsListPage: () => ({
    path: `/account-setting/news/list`,
    component: NewsListPage,
    permission: PermissionsEnum.NavigationNews,
    requiredLogin: true,
  }),

  newEditPage: ({ itemId } = { itemId: ':itemId' }): IPage => ({
    path: `/edit-new/${itemId}`,
    component: NewEditPage,
    permission: PermissionsEnum.NavigationNews,
    requiredLogin: true,
  }),
  documentListPage: () => ({
    path: `/document/list`,
    component: DocumentListPage,
    permission: PermissionsEnum.NavigationDocuments,
    requiredLogin: true,
  }),
  documentEditPage: ({ itemId } = { itemId: ':itemId' }): IPage => ({
    path: `/edit-document/${itemId}`,
    component: DocumentEditPage,
    permission: PermissionsEnum.NavigationDocuments,
    requiredLogin: true,
  }),
  reportingListPage: (params): IPage => ({
    path: `/reporting/list${varToStringParams({
      variablesArray: Object.keys(params ? params : {}).map(
        (key: string, index: number) => ({
          key: key,
          value: _.get(params, key, undefined),
        })
      ),
    })}`,
    component: ReportingListPage,
    permission: PermissionsEnum.NavigationReporting,
    requiredLogin: true,
  }),
  gameScenePage: (): IPage => ({
    path: `/game`,
    component: GameScene,
    requiredLogin: true,
  }),
};

export const AppbarsConfig: IAppbarsConfig = {
  primaryNav: {
    component: AppbarMenu,
    include: null,
    exclude: [
      // NavigationConfig.loginPage().path,
      // NavigationConfig.registerPage().path,
      NavigationConfig.forgotPasswordPage().path,
      NavigationConfig.resetPasswordPage().path,
    ],
    select: 'exclude',
  },
  secondaryNav: {
    component: AppbarMenu,
    include: [],
    exclude: 'all',
    select: 'exclude',
  },
};

export const Appbars: Array<INav> = Object.values(AppbarsConfig);
export default NavigationConfig;
